import { Tag } from "@/types/Tag";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Trans, useLingui } from "@lingui/react/macro";
import { DialogProps } from "@radix-ui/react-dialog";
import { Button } from "@components/ui/button";
import { MessageSquareIcon } from "lucide-react";
import { postTrackingData } from "@/api/endpoints/track";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemMedia,
  ItemTitle,
} from "@components/ui/item";
import { EVENT } from "@/tracking_events";
import { SearchFilterV4 } from "@/types/SearchFilter";
import { useMutation } from "@tanstack/react-query";
import {
  requestAsyncExport,
  ASYNC_EXPORT_FORMAT,
  AsyncExportFormat,
} from "@/api/endpoints/async-export";
import { toast } from "sonner";
import { Spinner } from "@components/ui/spinner";
import { useDelayedLoading } from "@/hooks/useDelayedLoading";

export default function TagExportDialog({
  isFavoritePage,
  filters,
  orgUuid,
  tag,
  ...rest
}: {
  isFavoritePage: boolean;
  filters: SearchFilterV4;
  orgUuid: Uuid;
  tag: Tag | undefined;
} & DialogProps) {
  const { t } = useLingui();

  const tagName = isFavoritePage ? t`sparade omnämnanden` : tag?.name;

  const { mutate: startAsyncExport, isPending } = useMutation({
    mutationFn: (format: AsyncExportFormat) => {
      if (!tag?.slug) throw new Error("Missing tag slug");

      const trackEvent =
        format === ASYNC_EXPORT_FORMAT.EXCEL
          ? EVENT.SHARE_TAG_CLICKED_EXCEL
          : EVENT.SHARE_TAG_CLICKED_CSV;
      postTrackingData(trackEvent);

      return requestAsyncExport({
        format,
        org_uuid: orgUuid,
        tag: tag.slug,
        filters,
      });
    },
    onSuccess: (res) => {
      if (!res.ok) throw new Error();

      toast.success(
        t`Exporten har startats. Du får ett mejl med en nedladdningslänk när den är klar.`
      );
      rest.onOpenChange?.(false);
    },
    onError: () => {
      toast.error(t`Kunde inte starta exporten. Försök igen.`);
    },
  });

  const { isVisible: showSpinner } = useDelayedLoading(isPending);

  return (
    <Dialog {...rest}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Exportera {tagName}</Trans>
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Trans>
              Exportera alla omnämnanden för <q>{tagName}</q>
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <Item variant="outline" size="sm">
          <ItemMedia variant="icon">
            <MessageSquareIcon />
          </ItemMedia>
          <ItemContent>
            <ItemTitle>
              <Trans>Omnämnanden</Trans>
            </ItemTitle>
          </ItemContent>
          <ItemActions>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={isPending}
              data-testid="excel-export-link"
              onClick={() => startAsyncExport(ASYNC_EXPORT_FORMAT.EXCEL)}
            >
              <Trans>Exportera till Excel</Trans>
              {showSpinner && <Spinner />}
            </Button>

            <Button
              type="button"
              size="sm"
              disabled={isPending}
              data-testid="csv-export-link"
              onClick={() => startAsyncExport(ASYNC_EXPORT_FORMAT.CSV)}
            >
              <Trans>Exportera till CSV</Trans>
              {showSpinner && <Spinner />}
            </Button>
          </ItemActions>
        </Item>
      </DialogContent>
    </Dialog>
  );
}
